import React from 'react';

import i18n from 'util/i18n';
import * as formTypes from 'constants/formTypes';

import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { SubstanceThcUsageItemModel } from 'forms/sections/substance/thc/usage/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import Dropdown from 'components/Form/Dropdown';
import { chargeTypes } from 'constants/enums/legalOptions';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';

interface Props extends ValidationElementProps, Partial<SubstanceThcUsageItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  requireThcWhileSafety?: boolean
  requireThcWithClearance?: boolean
  requireThcInFuture?: boolean
  formType: formTypes.FormTypes
  formVersion: string
}
export default class ThcUseItem extends ValidationElement<Props> {
  update = (values: Partial<SubstanceThcUsageItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({

        FirstUse: this.props.FirstUse,
        RecentUse: this.props.RecentUse,
        NatureOfUse: this.props.NatureOfUse,
        UseWhileEmployed: this.props.UseWhileEmployed,
        UseWithClearance: this.props.UseWithClearance,
        Explanation: this.props.Explanation,
        Comments: this.props.Comments,
        HowManyTimes: this.props.HowManyTimes,
        Reason: this.props.Reason,
        UseWhileEmployedPosition: this.props.UseWhileEmployedPosition,
        UseWhileEmployedExplanation: this.props.UseWhileEmployedExplanation,
        UseWithClearanceExplanation: this.props.UseWithClearanceExplanation,
        UseInFutureClearanceOrSafety: this.props.UseInFutureClearanceOrSafety,
        UseInFutureClearanceOrSafetyExplanation: this.props.UseInFutureClearanceOrSafetyExplanation,
        UseInFutureGovernment: this.props.UseInFutureGovernment,
        UseInFutureGovernmentExplanation: this.props.UseInFutureGovernmentExplanation,
        ...values,
      });
    }
  };

  updateFirstUse = (values: SubstanceThcUsageItemModel['FirstUse']) => {
    this.update({ FirstUse: values });
  };

  updateRecentUse = (values: SubstanceThcUsageItemModel['RecentUse']) => {
    this.update({ RecentUse: values });
  };

  updateNatureOfUse = (values: SubstanceThcUsageItemModel['NatureOfUse']) => {
    this.update({ NatureOfUse: values });
  };

  updateUseWhileEmployed = (values: SubstanceThcUsageItemModel['UseWhileEmployed']) => {
    this.update({ UseWhileEmployed: values });
  };

  updateUseWithClearance = (values: SubstanceThcUsageItemModel['UseWithClearance']) => {
    this.update({ UseWithClearance: values });
  };

  updateExplanation = (values: SubstanceThcUsageItemModel['Explanation']) => {
    this.update({ Explanation: values });
  };

  updateComments = (values: SubstanceThcUsageItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateHowManyTimes = (values: SubstanceThcUsageItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateReason = (values: SubstanceThcUsageItemModel['Reason']) => {
    this.update({ Reason: values });
  };

  updateUseWhileEmployedPosition = (values: SubstanceThcUsageItemModel['UseWhileEmployedPosition']) => {
    this.update({ UseWhileEmployedPosition: values });
  };

  updateUseWhileEmployedExplanation = (values: SubstanceThcUsageItemModel['UseWhileEmployedExplanation']) => {
    this.update({ UseWhileEmployedExplanation: values });
  };

  updateUseWithClearanceExplanation = (values: SubstanceThcUsageItemModel['UseWithClearanceExplanation']) => {
    this.update({ UseWithClearanceExplanation: values });
  };

  updateUseInFutureClearanceOrSafety = (values: SubstanceThcUsageItemModel['UseInFutureClearanceOrSafety']) => {
    this.update({ UseInFutureClearanceOrSafety: values });
  };

  updateUseInFutureClearanceOrSafetyExplanation = (values: SubstanceThcUsageItemModel['UseInFutureClearanceOrSafetyExplanation']) => {
    this.update({ UseInFutureClearanceOrSafetyExplanation: values });
  };

  updateUseInFutureGovernment = (values: SubstanceThcUsageItemModel['UseInFutureGovernment']) => {
    this.update({ UseInFutureGovernment: values });
  };

  updateUseInFutureGovernmentExplanation = (values: SubstanceThcUsageItemModel['UseInFutureGovernmentExplanation']) => {
    this.update({ UseInFutureGovernmentExplanation: values });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="thc-use">

        <Field
          title={i18n.t('substance.pvq.thc.use.heading.firstUse')}
          attribute={`${attribute}.FirstUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.FirstUse}
            name="FirstUse"
            attribute={`${attribute}.FirstUse`}
            className="first-use"
            hideDay
            minDateEqualTo
            onUpdate={this.updateFirstUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.thc.use.heading.recentUse')}
          attribute={`${attribute}.RecentUse`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.RecentUse}
            name="RecentUse"
            attribute={`${attribute}.RecentUse`}
            className="recent-use"
            hideDay
            minDate={this.props.FirstUse}
            prefix="drugUsage"
            minDateEqualTo
            onUpdate={this.updateRecentUse}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.use.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.thc.use.heading.reason')}
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            className="reason"
            onUpdate={this.updateReason}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>


        <Branch
          name="UseWithClearance"
          attribute={`${attribute}.UseWithClearance`}
          label={i18n.t('substance.pvq.thc.use.heading.useWithClearance')}
          labelSize="h4"
          className="use-with-clearance"
          {...this.props.UseWithClearance}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWithClearance}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.UseWithClearance?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.thc.use.heading.useWithClearanceExplanation')}
            attribute={`${attribute}.UseWithClearanceExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.UseWithClearanceExplanation}
              name="useWhileEmployedExplanation"
              attribute={`${attribute}.UseWithClearanceExplanation`}
              className="useWithClearanceExplanation"
              onUpdate={this.updateUseWithClearanceExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Branch
          {...this.props.UseWhileEmployed}
          name="UseWhileEmployed"
          attribute={`${attribute}.UseWhileEmployed`}
          label={i18n.t('substance.pvq.thc.use.heading.useWhileEmployed')}
          labelSize="h4"
          className="use-while-employed"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseWhileEmployed}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.UseWhileEmployed?.value === 'Yes' && (
          <>

            <Field
              title={i18n.t('substance.pvq.thc.use.heading.useWhileEmployedPosition')}

              attribute={`${attribute}.UseWhileEmployedPosition`}
              scrollIntoView={this.props.scrollIntoView}

              adjustFor="buttons"
              shrink
              className="no-margin-bottom"
            >
              <Dropdown
                attribute={`${attribute}.UseWhileEmployedPosition`}
                name="UseWhileEmployedPosition"
                className="UseWhileEmployedPosition"
                onError={this.props.onError}
                onUpdate={this.updateUseWhileEmployedPosition}
                value={this.props.UseWhileEmployedPosition?.value}
                required={this.props.required}
              >
                {criminalJusticeorPublicSafetyPosition.map((s) => {
                  return (<option key={s} value={s}>
                    {s}
                  </option>);
                })}
              </Dropdown>
            </Field>

            <Field
              title={i18n.t('substance.pvq.thc.use.heading.useWhileEmployedExplanation')}
              attribute={`${attribute}.UseWhileEmployedExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.UseWhileEmployedExplanation}
                name="useWhileEmployedExplanation"
                attribute={`${attribute}.UseWhileEmployedExplanation`}
                className="useWhileEmployedExplanation"
                onUpdate={this.updateUseWhileEmployedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </>
        )}
        {/* According to the PDF the below fields do NOT belong in this subsection, should be Public Safety
Leaving it as it is for now as unaware of the inital dev's intention for doing so
*/}

        <Branch
          {...this.props.UseInFutureClearanceOrSafety}
          name="UseInFutureClearanceOrSafety"
          attribute={`${attribute}.UseInFutureClearanceOrSafety`}
          label={i18n.t('substance.pvq.thc.use.heading.useInFutureClearanceOrSafety')}
          labelSize="h4"
          className="use-in-future"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseInFutureClearanceOrSafety}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Field
          title={i18n.t('substance.pvq.thc.use.heading.useInFutureClearanceOrSafetyExplanation')}
          attribute={`${attribute}.UseInFutureClearanceOrSafetyExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.UseInFutureClearanceOrSafetyExplanation}
            name="UseInFutureExplanation"
            attribute={`${attribute}.UseInFutureExplanation`}
            className="useInFutureExplanation"
            onUpdate={this.updateUseInFutureClearanceOrSafetyExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          {...this.props.UseInFutureGovernment}
          name="UseInFutureGovernment"
          attribute={`${attribute}.UseInFutureGovernment`}
          label={i18n.t('substance.pvq.thc.use.heading.useInFutureGovernment')}
          labelSize="h4"
          className="use-in-future"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateUseInFutureGovernment}
          scrollIntoView={this.props.scrollIntoView}
        />

        <Field
          title={i18n.t('substance.pvq.thc.use.heading.useInFutureGovernmentExplanation')}
          attribute={`${attribute}.UseInFutureGovernmentExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.UseInFutureGovernmentExplanation}
            name="UseInFutureGovernmentExplanation"
            attribute={`${attribute}.UseInFutureGovernmentExplanation`}
            className="useInFutureGovernmentExplanation"
            onUpdate={this.updateUseInFutureGovernmentExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

      </div>
    );
  }
}

ThcUseItem.defaultProps = {
  UseWhileEmployed: {},
  UseWhileClearance: {},
  UseWhileFuture: {},
  Comments: {},
  ThcTypeOther: {},
  HowManyTimes: {},
  Reason: {},
  UseWhileEmployedPosition: {},
  UseWhileEmployedExplanation: {},
  UseWithClearanceExplanation: {},
  UseInFutureExplanation: {},
  onError: (value, arr: ErrorCode[]) => arr,
};
